import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Badge, Spinner } from 'react-bootstrap';
import LeadCard from './LeadCard';
import { Droppable } from './Droppable';
import axios from 'axios';

const LEADS_PER_PAGE = 10;

const MarketingKanbanColumn = ({
    column,
    leads: initialLeads,
    totalLeads,
    entidadeId,
    filtrosAvancados = {}, // ✅ único filtro — sem filtroResponsavel separado
    onCardClick,
    onWhatsAppClick,
    isDropDisabled,
}) => {
    const [leads, setLeads] = useState(initialLeads || []);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(
        (initialLeads?.length || 0) < (totalLeads || 0)
    );
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Refs para evitar stale closure
    const isLoadingRef = useRef(false);
    const hasMoreRef   = useRef(hasMore);
    const pageRef      = useRef(page);
    const leadsRef     = useRef(leads);

    useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
    useEffect(() => { pageRef.current    = page;    }, [page]);
    useEffect(() => { leadsRef.current   = leads;   }, [leads]);

    // Guarda cleanup do scroll listener
    const cleanupScrollRef = useRef(null);

    // ── Reset quando filtros ou coluna mudam ─────────────────────────────
    // ✅ resetKey usa apenas filtrosAvancados (sem filtroResponsavel separado)
    const resetKeyRef = useRef(`${column.id}__${JSON.stringify(filtrosAvancados)}`);

    useEffect(() => {
        const newKey = `${column.id}__${JSON.stringify(filtrosAvancados)}`;
        const contextMudou = resetKeyRef.current !== newKey;

        if (contextMudou) {
            // Filtro ou coluna mudou → reset completo
            resetKeyRef.current = newKey;
            setLeads(initialLeads || []);
            setPage(1);
            setHasMore((initialLeads?.length || 0) < (totalLeads || 0));
            return;
        }

        // Mesmo contexto (polling silencioso ou DnD):
        // Atualiza p.1 sem destruir páginas extras já carregadas
        setLeads(prev => {
            if (prev.length <= LEADS_PER_PAGE) {
                return initialLeads || [];
            }
            const initialIds = new Set((initialLeads || []).map(l => l.id));
            const paginasExtras = prev.filter(l => !initialIds.has(l.id));
            return [...(initialLeads || []), ...paginasExtras];
        });

        setHasMore(leadsRef.current.length < (totalLeads || 0));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialLeads, totalLeads, column.id, filtrosAvancados]);

    // ── loadMore ─────────────────────────────────────────────────────────
    const loadMore = useCallback(async () => {
        if (isLoadingRef.current || !hasMoreRef.current) return;

        isLoadingRef.current = true;
        setIsLoadingMore(true);

        try {
            const nextPage = pageRef.current + 1;
            const params = {
                entidade_id: entidadeId,
                estagio_id:  column.id,
                page:        nextPage,
                per_page:    LEADS_PER_PAGE,
            };

            // ✅ Todos os filtros incluindo responsavel_id e busca
            if (filtrosAvancados.responsavel_id) params.responsavel_id = filtrosAvancados.responsavel_id;
            if (filtrosAvancados.busca)          params.busca           = filtrosAvancados.busca;
            if (filtrosAvancados.status)         params.status          = filtrosAvancados.status;
            if (filtrosAvancados.prioridade)     params.prioridade      = filtrosAvancados.prioridade;
            if (filtrosAvancados.tag)            params.tag             = filtrosAvancados.tag;
            if (filtrosAvancados.perdido_motivo) params.perdido_motivo  = filtrosAvancados.perdido_motivo;
            if (filtrosAvancados.criado_de)      params.criado_de       = filtrosAvancados.criado_de;
            if (filtrosAvancados.criado_ate)     params.criado_ate      = filtrosAvancados.criado_ate;
            if (filtrosAvancados.atualizado_de)  params.atualizado_de   = filtrosAvancados.atualizado_de;
            if (filtrosAvancados.atualizado_ate) params.atualizado_ate  = filtrosAvancados.atualizado_ate;
            if (filtrosAvancados.fechamento_de)  params.fechamento_de   = filtrosAvancados.fechamento_de;
            if (filtrosAvancados.fechamento_ate) params.fechamento_ate  = filtrosAvancados.fechamento_ate;

            const response = await axios.get('/api/leads', { params });
            const novosLeads = response.data.leads || [];

            setLeads(prev => {
                const ids    = new Set(prev.map(l => l.id));
                const unicos = novosLeads.filter(l => !ids.has(l.id));
                return [...prev, ...unicos];
            });

            setPage(nextPage);
            setHasMore(nextPage < response.data.last_page);
        } catch (err) {
            console.error('Erro ao carregar mais leads:', err);
        } finally {
            isLoadingRef.current = false;
            setIsLoadingMore(false);
        }
    // ✅ filtrosAvancados nas deps para capturar busca e outros filtros
    }, [column.id, entidadeId, filtrosAvancados]);

    // Ref estável para o loadMore mais recente
    const loadMoreRef = useRef(loadMore);
    useEffect(() => { loadMoreRef.current = loadMore; }, [loadMore]);

    // ── Callback ref: registra o listener no momento exato da montagem ───
    const setScrollRef = useCallback((el) => {
        if (cleanupScrollRef.current) {
            cleanupScrollRef.current();
            cleanupScrollRef.current = null;
        }

        if (!el) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = el;
            const distancia = scrollHeight - scrollTop - clientHeight;
            if (distancia < 80) {
                loadMoreRef.current();
            }
        };

        el.addEventListener('scroll', handleScroll, { passive: true });
        cleanupScrollRef.current = () => el.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <Card className="kanban-column shadow-sm">
            <Card.Header
                className="d-flex justify-content-between align-items-center"
                style={{ backgroundColor: column.color, color: 'white' }}
            >
                <span className="fw-bold">{column.title}</span>
                <Badge bg="light" text="dark" pill>
                    {totalLeads ?? leads.length}
                </Badge>
            </Card.Header>

            <Card.Body className="kanban-column-body p-0">
                <Droppable droppableId={column.id} isDropDisabled={isDropDisabled}>
                    {(provided, snapshot) => (
                        <div
                            {...provided.droppableProps}
                            ref={(el) => {
                                provided.innerRef(el);
                                setScrollRef(el);
                            }}
                            className="kanban-column-content"
                            style={{
                                background: snapshot.isDraggingOver
                                    ? '#e9ecef'
                                    : 'transparent',
                                minHeight: '500px',
                                padding: '8px',
                            }}
                        >
                            {leads.map((lead, index) => (
                                <LeadCard
                                    key={lead.id}
                                    lead={lead}
                                    index={index}
                                    onClick={onCardClick}
                                    onWhatsAppClick={onWhatsAppClick}
                                />
                            ))}
                            {provided.placeholder}

                            {isLoadingMore && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    padding: '12px 0',
                                }}>
                                    <Spinner animation="border" size="sm" variant="secondary" />
                                </div>
                            )}

                            {!hasMore && leads.length > LEADS_PER_PAGE && (
                                <div style={{
                                    textAlign: 'center',
                                    fontSize: `calc(11px * var(--font-scale))`,
                                    color: '#9ca3af',
                                    padding: '8px 0',
                                    borderTop: '1px solid #f3f4f6',
                                    marginTop: '4px',
                                }}>
                                    ✓ Todos os {leads.length} leads carregados
                                </div>
                            )}
                        </div>
                    )}
                </Droppable>
            </Card.Body>
        </Card>
    );
};

export default MarketingKanbanColumn;
